/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import {cloneElement, type FC, useCallback, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useImpression} from '~/common/hooks/useImpression';
import {willChildReturnNull} from '~/common/utils';
import {useConnectEditor} from '~/ViewInvitation/hooks/useConnectEditor';
import {
  selectHostUploadProcessing,
  selectHostUploadProgress,
  selectSharedUploadProcessing,
  selectSharedUploadProgress,
} from '~/ViewInvitation/selectors/photos';
import type {ModuleProps} from '~/ViewInvitation/types/module';

import './Module.sass';

export const Module: FC<ModuleProps> = ({
  children,
  moduleName,
  impressionEvent = undefined,
  preview = false,
  readonly,
  highlightOnHover = false,
  isPremiumEvent = false,
  ...acyclicalProps
}) => {
  const tracking: Record<string, any> = {};
  const trackingRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const hostUploadProgress = useSelector(selectHostUploadProgress);
  const sharedUploadProgress = useSelector(selectSharedUploadProgress);
  const hostUploadProcessing = useSelector(selectHostUploadProcessing);
  const sharedUploadProcessing = useSelector(selectSharedUploadProcessing);

  const uploadProgress = useMemo(() => {
    if (moduleName === 'host_photo') return hostUploadProgress;
    if (moduleName === 'photo') return sharedUploadProgress;
    return undefined;
  }, [moduleName, hostUploadProgress, sharedUploadProgress]);

  const processing = useMemo(() => {
    if (moduleName === 'host_photo') return hostUploadProcessing;
    if (moduleName === 'photo') return sharedUploadProcessing;
    return undefined;
  }, [moduleName, hostUploadProcessing, sharedUploadProcessing]);

  if (impressionEvent) {
    tracking.ref = trackingRef;
    // biome-ignore lint/correctness/useHookAtTopLevel: existing functionality
    useImpression(trackingRef, impressionEvent);
  }

  const [moduleAnimation] = useAutoAnimate({
    duration: 350,
    easing: 'ease-in-out',
  });

  const allowTriggerEdit = useCallback(
    (ev: Event) => {
      switch (moduleName) {
        case 'host_photo': {
          const carousel = document.querySelector('[data-qa-id="photos-carousel"]');
          return !carousel?.contains(ev.target as Node);
        }
        default:
          return true;
      }
    },
    [moduleName]
  );

  const editable = preview && !readonly;
  useConnectEditor(containerRef, editBtnRef, editable && highlightOnHover, allowTriggerEdit);

  const setContainerRef = useCallback(
    (elem: HTMLDivElement) => {
      containerRef.current = elem;
      moduleAnimation(elem);
    },
    [moduleAnimation]
  );

  const showProgress = typeof uploadProgress === 'number';
  const style = {
    width: `${uploadProgress}%`,
    opacity: uploadProgress ? 1 : 0,
  } as React.CSSProperties;

  if (willChildReturnNull(children)) {
    return null;
  }

  return (
    <div
      className={cx(`module ${moduleName}`, {
        preview,
        'module--processing': processing,
        editable: editable && highlightOnHover,
        premium: isPremiumEvent,
      })}
      data-qa-id={`${moduleName}-module`}
      id={`module-${moduleName}`}
      {...tracking}
      ref={setContainerRef}
    >
      {highlightOnHover && <div className="module__edit-hover" />}
      {showProgress && <div className="module__upload-progress" style={style} />}
      {cloneElement(children, {
        ...acyclicalProps,
        readonly,
        editBtnRef,
        isCreationFlow: !window.location.pathname.startsWith('/event'),
      })}
    </div>
  );
};
